// Selling Falcon (approved 29/10/2026): the subscription agreement a new customer signs online, from a deal in the
// Falcon owner's own sales organisation ("Falcon HQ") or from Falcon Console → Agreements. Shared by the server (which
// fills it in, keeps it and makes the signed PDF) and the screens.

/** What can be sold: Falcon PLM, Falcon CRM or both, per user per month in GBP (5 users at least). No VAT. */
export const AGREEMENT_PACKAGES = [
  { key: "plm", label: "Falcon PLM", price: 99, plm: true, crm: false },
  { key: "crm", label: "Falcon CRM", price: 49, plm: false, crm: true },
  { key: "both", label: "Falcon PLM + CRM", price: 129, plm: true, crm: true },
] as const;
export type AgreementPackage = (typeof AGREEMENT_PACKAGES)[number]["key"];
export const MIN_USERS = 5;
/** Onboarding, paid once: none, the standard price or the introductory offer. */
export const ONBOARDING_CHOICES = [
  { key: "none", label: "No onboarding", price: 0 },
  { key: "standard", label: "Onboarding £1,500, paid once", price: 1500 },
  { key: "intro", label: "Onboarding (introductory offer) £1,000, paid once", price: 1000 },
] as const;
export type OnboardingChoice = (typeof ONBOARDING_CHOICES)[number]["key"];

export interface AgreementDetails {
  organisation: string;
  package: AgreementPackage;
  users: number;
  /** Price per user per month (GBP). Starts at the package price; can be changed for a special offer. */
  price: number;
  /** YYYY-MM-DD */
  start: string;
  onboarding: OnboardingChoice;
  /** The onboarding price when it was sent (from Console → Website Prices, 3.85). Older agreements: the fixed price. */
  onboardingPrice?: number;
}

export const AGREEMENT_STATUS_LABEL: Record<string, string> = { sent: "Waiting to be signed", opened: "Opened, not signed", signed: "Signed", cancelled: "Cancelled", expired: "Link expired" };
/** Days the signing link works for. */
export const AGREEMENT_LINK_DAYS = 30;

/** The words that are filled in when an agreement is sent, written {organisation} etc. (shown under the wording editor). */
export const AGREEMENT_FILL_INS: Array<[string, string]> = [
  ["organisation", "The customer's organisation"], ["package", "Falcon PLM, Falcon CRM or Falcon PLM + CRM"], ["users", "Number of users"],
  ["price", "Price per user per month"], ["monthly", "Monthly total"], ["start", "Start date"], ["onboarding", "Onboarding line"],
  ["supplier", "Your company name (Console → Legal details)"], ["notice", "Notice to cancel (Legal details)"], ["terms", "Link to the Terms of service"],
];

export const DEFAULT_AGREEMENT_TITLE = "Falcon subscription agreement";
export const DEFAULT_AGREEMENT_BODY = `This agreement is between {supplier} ("Falcon", "we") and {organisation} ("you").

# 1. What you get
You subscribe to {package} for {users} users at {price} per user per month, {monthly} a month in total, starting on {start}.
{onboarding}

# 2. Paying
You pay monthly in advance in pounds sterling (GBP). If a payment is more than 7 days late we may pause your account. It opens again as soon as the payment arrives. Your data is kept while the account is paused.

# 3. Users
You can add users at any time. Each user is charged at the same price from the month they are added. The minimum is ${MIN_USERS} users.

# 4. Your data
Your data stays yours. You can download all of it at any time from Settings. We look after it as our Privacy policy and Data processing agreement say.

# 5. Ending the agreement
Either of us can end this agreement by giving notice: {notice}. You can download your data before the account closes.

# 6. Terms of service
The Falcon Terms of service at {terms} also apply. If this agreement and the Terms of service say different things, this agreement wins.

# 7. Law
This agreement is under the law of England and Wales.`;

const gbp = (n: number) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
export const formatGbp = gbp;
export const packageOf = (k: string) => AGREEMENT_PACKAGES.find((p) => p.key === k) || AGREEMENT_PACKAGES[2];
export const onboardingOf = (k: string) => ONBOARDING_CHOICES.find((o) => o.key === k) || ONBOARDING_CHOICES[0];
/** The onboarding price of one agreement: the price kept when it was sent, or (older agreements) the fixed price. */
export const onboardingPriceOf = (d: AgreementDetails) => (d.onboarding === "none" ? 0 : typeof d.onboardingPrice === "number" ? d.onboardingPrice : onboardingOf(d.onboarding).price);
export const ukDate = (iso: string) => (/^\d{4}-\d{2}-\d{2}/.test(iso || "") ? iso.slice(0, 10).split("-").reverse().join("/") : iso || "");

/** One line per detail, as the email, the signing page and the PDF show them. */
export function detailRows(d: AgreementDetails): Array<[string, string]> {
  const ob = { price: onboardingPriceOf(d) };
  return [
    ["Organisation", d.organisation], ["Package", packageOf(d.package).label], ["Users", String(d.users)],
    ["Price", `${gbp(d.price)} per user per month`], ["Monthly total", gbp(d.price * d.users)], ["Starts", ukDate(d.start)],
    ["Onboarding", ob.price ? `${gbp(ob.price)}, paid once${d.onboarding === "intro" ? " (introductory offer)" : ""}` : "None"],
  ];
}

/** The wording with the details filled in. */
export function fillAgreement(body: string, d: AgreementDetails, extra: { supplier: string; notice: string; terms: string }): string {
  const ob = { price: onboardingPriceOf(d) };
  const onboarding = ob.price ? `Onboarding${d.onboarding === "intro" ? " (introductory offer)" : ""}: ${gbp(ob.price)}, paid once.` : "No onboarding is included.";
  const values: Record<string, string> = {
    organisation: d.organisation, package: packageOf(d.package).label, users: String(d.users), price: gbp(d.price), monthly: gbp(d.price * d.users),
    start: ukDate(d.start), onboarding, supplier: extra.supplier || "Falcon", notice: extra.notice || "30 days", terms: extra.terms,
  };
  return body.replace(/\{(organisation|package|users|price|monthly|start|onboarding|supplier|notice|terms)\}/g, (_m, k: string) => values[k]);
}

/** The wording as blocks: "# " starts a heading; blank lines separate paragraphs. */
export function agreementBlocks(text: string): Array<{ heading: boolean; text: string }> {
  const out: Array<{ heading: boolean; text: string }> = [];
  let para: string[] = [];
  const flush = () => { if (para.length) out.push({ heading: false, text: para.join(" ").replace(/\s+/g, " ").trim() }); para = []; };
  for (const raw of text.replace(/\r/g, "").split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("#")) { flush(); out.push({ heading: true, text: line.replace(/^#+\s*/, "") }); continue; }
    // A line under the previous one stays its own paragraph when it ends a sentence.
    if (para.length && /[.:!?]$/.test(para[para.length - 1])) flush();
    para.push(line);
  }
  flush();
  return out.filter((b) => b.text);
}

/** Which status an agreement shows: signed, cancelled, link expired, opened or waiting. */
export function agreementState(a: { status: string; openedAt?: string | null; expiresAt: string }, now = new Date()): string {
  if (a.status !== "sent") return a.status;
  if (new Date(a.expiresAt).getTime() < now.getTime()) return "expired";
  return a.openedAt ? "opened" : "sent";
}
